import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import type { PatchTicket, PatchTicketSettings } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'
import { chipClass, severityLabel, useIsTenantAdmin } from './labels'

function statoLabel(s: string): string {
  switch (s) {
    case 'open': return t('aperto')
    case 'closed': return t('chiuso da CENSO')
    default: return t('non aperto')
  }
}

/**
 * Tickets on the tenant's ITSM (patch_ticket_service): one per patch, on
 * ServiceNow or Jira through a connector of the tenant, opened from the
 * catalogue or by the rule, closed by CENSO when the patch is installed on
 * every machine of the ticket.
 */
export function Tickets() {
  const state = useAsync(() => api.patchTickets(), [])
  const admin = useIsTenantAdmin()
  const [v, setV] = useState<PatchTicketSettings | null>(null)
  const [campi, setCampi] = useState('')
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const d = state.data

  useEffect(() => {
    if (!d) return
    setV(d.settings)
    setCampi(Object.entries(d.settings.fields).map(([k, x]) => `${k}=${x}`).join('\n'))
  }, [d])

  const salva = async () => {
    if (!v) return
    setLavoro(true)
    setErrore(null)
    setFatto(null)
    try {
      const fields: Record<string, string> = {}
      for (const riga of campi.split('\n')) {
        const i = riga.indexOf('=')
        if (i > 0) fields[riga.slice(0, i).trim()] = riga.slice(i + 1).trim()
      }
      await api.patchTicketSettings({ ...v, fields })
      setFatto(t('Impostazioni salvate.'))
      state.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const sistema = d?.connectors.find((c) => c.id === v?.connector_id)?.kind
  const colonne: Column<PatchTicket>[] = [
    { key: 'number', header: t('Ticket'), value: (r) => r.number,
      render: (r) => (r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.number || r.id.slice(0, 8)}</a> : r.number || '—') },
    { key: 'system', header: t('Sistema'), value: (r) => (r.system === 'jira' ? 'Jira' : 'ServiceNow'), filter: 'select' },
    { key: 'patch', header: t('Patch'), value: (r) => r.patch_name || r.patch_key, render: (r) => <span className="mono">{r.patch_name || r.patch_key}</span> },
    { key: 'machines', header: t('Macchine'), value: (r) => r.machines },
    { key: 'status', header: t('Stato'), value: (r) => statoLabel(r.status), filter: 'select',
      render: (r) => <span title={r.error ?? ''} className={chipClass(r.status === 'open' ? 'info' : r.status === 'closed' ? 'ok' : 'warn')}>{statoLabel(r.status)}</span> },
    { key: 'opened', header: t('Aperto'), type: 'date', value: (r) => r.opened_at,
      render: (r) => <>{formatDateTime(r.opened_at)}<div className="text-muted patch-sub">{r.auto ? t('dalla regola') : r.opened_by}</div></> },
    { key: 'closed', header: t('Chiuso'), type: 'date', value: (r) => r.closed_at, render: (r) => formatDateTime(r.closed_at) },
    { key: 'error', header: t('Errore'), value: (r) => r.error ?? '', render: (r) => (r.error ? <span className="muted" title={r.error}>{r.error.slice(0, 80)}</span> : '') },
  ]

  return (
    <>
      <Card title={t('Ticket sul sistema di gestione')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Un ticket per patch su ServiceNow o Jira, con il connettore che il tenant ha già (Integrazioni → Connettori): cosa installare, su quali macchine, perché (CVE, rischio, EPSS) ed entro quando (lo SLA), con il collegamento alla console. Si apre dalla riga della patch nel catalogo o lo apre la regola qui sotto; CENSO lo chiude da solo quando la patch non manca più su nessuna delle macchine del ticket.')}
        </p>
        <ErrorBanner error={errore ?? state.error} />
        {fatto && <div className="banner banner--ok">{fatto}</div>}
        {!d || !v ? !state.error && <Spinner /> : (
          <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); void salva() }}>
            <div className="form-grid">
              <label className="field span-2"><span className="field__label">{t('Connettore')}</span>
                <select value={v.connector_id ?? ''} disabled={!admin} onChange={(e) => setV({ ...v, connector_id: e.target.value || null })}>
                  <option value="">{t('nessuno: niente ticket')}</option>
                  {d.connectors.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.kind === 'jira' ? 'Jira' : 'ServiceNow'})</option>)}
                </select></label>
              {sistema === 'servicenow' && (
                <>
                  <label className="field span-2"><span className="field__label">{t('Tabella')}</span>
                    <input type="text" value={v.table} disabled={!admin} onChange={(e) => setV({ ...v, table: e.target.value })} placeholder={'incident'} /></label>
                  <label className="field span-4"><span className="field__label">{t('Campi in più (uno per riga, campo=valore)')}</span>
                    <textarea value={campi} disabled={!admin} onChange={(e) => setCampi(e.target.value)} rows={3}
                              placeholder={'assignment_group=Server Team\ncategory=software'} /></label>
                </>
              )}
              {sistema === 'jira' && (
                <>
                  <label className="field span-1"><span className="field__label">{t('Progetto (chiave)')}</span>
                    <input type="text" value={v.project} disabled={!admin} onChange={(e) => setV({ ...v, project: e.target.value })} placeholder={'IT'} required /></label>
                  <label className="field span-1"><span className="field__label">{t('Tipo di issue')}</span>
                    <input type="text" value={v.issue_type} disabled={!admin} onChange={(e) => setV({ ...v, issue_type: e.target.value })} placeholder={'Task'} /></label>
                  <label className="field span-2"><span className="field__label">{t('Etichette (virgola)')}</span>
                    <input type="text" value={v.labels.join(', ')} disabled={!admin} onChange={(e) => setV({ ...v, labels: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} /></label>
                  <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={v.jira_priority} disabled={!admin} onChange={(e) => setV({ ...v, jira_priority: e.target.checked })} />
                    <span>{t('Priorità dalla gravità (Highest, High, Medium, Low)')}</span></label>
                </>
              )}
            </div>
            <h4 className="form__sezione">{t('Regola: apri da solo')}</h4>
            <div className="form-grid">
              <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={v.auto.enabled} disabled={!admin || !v.connector_id} onChange={(e) => setV({ ...v, auto: { ...v.auto, enabled: e.target.checked } })} />
                <span>{t('Apri un ticket per ogni patch che risponde alla regola (al più 20 per giro)')}</span></label>
              <label className="field span-1"><span className="field__label">{t('Gravità minima')}</span>
                <select value={v.auto.min_severity} disabled={!admin} onChange={(e) => setV({ ...v, auto: { ...v.auto, min_severity: e.target.value } })}>
                  {['critica', 'alta', 'media', 'bassa'].map((s) => <option key={s} value={s}>{severityLabel(s)}</option>)}
                </select></label>
              <label className="field span-1" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={v.auto.kev} disabled={!admin} onChange={(e) => setV({ ...v, auto: { ...v.auto, kev: e.target.checked } })} />
                <span>{t('anche le sfruttate (KEV)')}</span></label>
              <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={v.auto.overdue_only} disabled={!admin} onChange={(e) => setV({ ...v, auto: { ...v.auto, overdue_only: e.target.checked } })} />
                <span>{t('solo quelle già oltre lo SLA')}</span></label>
            </div>
            {admin && <div className="form-dialog__azioni"><button type="submit" disabled={lavoro}>{lavoro ? t('Salvo…') : t('Salva')}</button></div>}
          </form>
        )}
      </Card>
      <Card title={t('Ticket aperti da CENSO')}>
        {!d ? !state.error && <Spinner /> : d.tickets.length === 0
          ? <EmptyState title={t('Nessun ticket ancora.')} />
          : <DataTable rows={d.tickets} columns={colonne} rowKey={(r) => r.id} conteggio={{ nome: t('ticket') }} />}
      </Card>
    </>
  )
}
